import { useState } from "react";
import {
  makeGetApiCall,
  makePostApiCall,
  makePostApiCallToPrinter,
  makePutApiCall,
} from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import DeleteReceivingItemByBarcodeForm from "../../components/forms/receiving/DeleteReceivingItemByBarcodeForm";
import {
  generateUniqueNumber,
  updateObjectInArray,
} from "../../shared/utility";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";

export function ReceivingInvoiceDetailsViewModel(
  logout,
  receivingId,
  addSnackbar,
) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [truckOptions, setTruckOptions] = useState([]);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [carrierOptions, setCarrierOptions] = useState([]);
  const [poOptions, setPoOptions] = useState([]);
  const [receiving, setReceiving] = useState({ status: 0 });
  const [selectedItems, setSelectedItems] = useState(null);
  const [rowDetails, setRowDetails] = useState({}); // { [rowId]: detailData }
  const [loadingRows, setLoadingRows] = useState({}); // { [rowId]: boolean }
  const [subRowDetails, setSubRowDetails] = useState({}); // { [rowId]: detailData }
  const [subLoadingRows, setSubLoadingRows] = useState({}); // { [rowId]: boolean }
  const [lastRow, setLastRow] = useState(null);

  const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

  const getReceivingInvoiceDetails = async function (page = 1) {
    const endpoint =
      page && page > 1
        ? `receivingInvoices/${receivingId}?page=${page}`
        : `receivingInvoices/${receivingId}`;
    let callResult = await makeGetApiCall(endpoint);

    if (callResult.success) {
      setReceiving(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const updateReceivingInvoice = async function (payload) {
    const body = {
      invoice_number: payload.invoice_number,
      truck_selected: payload.truck_selected,
      supplier_selected: payload.supplier_selected,
      carrier_selected: payload.carrier_selected,
      status: payload.statusSelected.value,
      truck_question_1: payload.truck_question_1.value,
      truck_question_2: payload.truck_question_2.value,
      truck_question_3: payload.truck_question_3.value,
      truck_question_4: payload.truck_question_4.value,
    };

    const hasUploadedPdf =
      Array.isArray(payload.uploaded_pdf) &&
      payload.uploaded_pdf[0] instanceof File;
    const contentType = hasUploadedPdf
      ? "multipart/form-data"
      : "application/json";
    if (hasUploadedPdf) {
      body.uploaded_pdf = payload.uploaded_pdf;
    }

    let callResult = await makePostApiCall(
      `updateReceivingInvoices/${receiving.id}`,
      body,
      contentType,
    );

    if (callResult.success) {
      await getReceivingInvoiceDetails();
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addItemToReceiving = async function (values) {
    const { is_paginated, page, parentRow, ...formValues } = values;

    const payload = {
      invoice_id: receiving.id,
      lot_number: receiving.lot_number,
      ...formValues,
    };
    let callResult = await makePostApiCall("receivingItems/create", payload);

    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const refreshPage = is_paginated && page ? page : 1;

      if (parentRow) {
        await getReceivingInvoiceDetails(refreshPage);

        const warehouseLocationId =
          formValues.warehouse_location_id?.value ??
          formValues.warehouse_location_id;
        const sameGroup =
          formValues.lot_number === parentRow.lot_number &&
          Number(warehouseLocationId) ===
            Number(parentRow.warehouse_location_id);

        if (sameGroup) {
          await fetchRowDetails(parentRow);
        }
      } else if (is_paginated && page) {
        await getReceivingInvoiceDetails(page);
      } else {
        setReceiving((prev) => {
          const currReceivingItems = prev.items ? prev.items : [];
          return {
            ...prev,
            items: [callResult.data.item, ...currReceivingItems],
          };
        });
      }

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Item added",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  const showInlineSaveError = (message) => {
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        message,
        SnackbarType.Error,
      ),
    );
  };

  const getProductOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`productsForDropdown`);

    if (callResult.success) {
      setProductOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }

    return callResult;
  };

  const getEmployeeOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`usersForDropdown`);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
    }
  };

  const getTruckOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`allReceivingTrucksForDropdown`);

    if (callResult.success) {
      setTruckOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch trucks");
    }
  };

  const getSupplierOptionsForDropdown = async function () {
    let callResult = await makeGetApiCall(`suppliersForDropdown`);

    if (callResult.success) {
      setSupplierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch suppliers");
    }
  };

  const onFetchProductDetails = async function (productId) {
    let callResult = await makeGetApiCall(`products/${productId}`);

    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch product");
    }
    return callResult;
  };

  const deleteReceivingItemById = async function (deletedItem) {
    let callResult = await makePostApiCall(
      `deleteReceivingItem/${deletedItem.id}`,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const currentItems = [...receiving.items];

      var itemIndex = currentItems.findIndex(
        (it) =>
          it.plu === deletedItem.plu &&
          it.lot_number === deletedItem.lot_number,
      );
      var itemToUpdate = currentItems[itemIndex];

      const newSubItems = itemToUpdate.subItems.filter(
        (it) => it.id !== deletedItem.id,
      );

      itemToUpdate = {
        ...itemToUpdate,
        carton_count: itemToUpdate.carton_count - 1,
        subItems: newSubItems,
      };

      var payload = {
        index: itemIndex,
        item: itemToUpdate,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setReceiving((prev) => ({ ...prev, items: newItems }));
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const deleteReceivingItemByBarcode = async function (barcode) {
    let callResult = await makePostApiCall(
      `deleteReceivingItemByBarcode/${barcode}`,
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const deletedItem = callResult.data.item;
      const currentItems = [...receiving.items];

      var itemIndex = currentItems.findIndex(
        (it) =>
          it.plu === deletedItem.plu &&
          it.lot_number === deletedItem.lot_number,
      );
      var itemToUpdate = currentItems[itemIndex];

      const newSubItems = itemToUpdate.subItems.filter(
        (it) => it.id !== deletedItem.id,
      );

      itemToUpdate = {
        ...itemToUpdate,
        carton_count: itemToUpdate.carton_count - 1,
        subItems: newSubItems,
      };

      var payload = {
        index: itemIndex,
        item: itemToUpdate,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setReceiving((prev) => ({ ...prev, items: newItems }));

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askDeleteReceivingItemById = function (item) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteReceivingItemById(item)}
          message={`Are you sure you want to return  ${item.product_name}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const updateReceivingItem = async function (payload) {
    const { parentRow, is_paginated, page, ...apiPayload } = payload;

    let callResult = await makePutApiCall(
      `updateReceivingItem/${apiPayload.id}`,
      {
        total_weight_in_kg: apiPayload.total_weight_in_kg,
        total_weight_in_lbs: apiPayload.total_weight_in_lbs,
        total_pieces: apiPayload.total_pieces,
        unit_cost: apiPayload.unit_cost,
        total_cost: apiPayload.total_cost,
      },
    );
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const refreshRequests = [
        getReceivingInvoiceDetails(is_paginated && page ? page : 1),
      ];

      if (parentRow && rowDetails[parentRow.unique_id]) {
        refreshRequests.push(fetchRowDetails(parentRow));
      }

      await Promise.all(refreshRequests);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showInlineSaveError(
          callResult.data?.message || "Could not update receiving item",
        );
      }
    }
    return callResult;
  };

  const openDeleteItemByBarcodePopup = function () {
    setEmptyModelOpen({
      childComponent: (
        <DeleteReceivingItemByBarcodeForm
          barcodeOptions={receiving.barcodes}
          buttonMessage="Delete"
          confirmAction={(barcode) => deleteReceivingItemByBarcode(barcode)}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const getWarehouses = async function () {
    let callResult = await makeGetApiCall("warehousesForDropdown");
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch warehouses");
    }
    return callResult;
  };

  const getWarehouseLocations = async function (warehouseId) {
    let callResult = await makeGetApiCall(
      `warehouseLocationsForDropdown/${warehouseId}`,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal("Could not fetch warehouse locations");
    }
    return callResult;
  };

  const onDeleteReceivingInvoice = async function () {
    let callResult = await makePostApiCall(
      `deleteReceivingInvoice/${receiving.id}`,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Receiving invoice deleted",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteReceivingInvoiceImage = async function (image) {
    let callResult = await makePostApiCall(`deleteReceivingInvoiceImage`, {
      invoice_id: receiving.id,
      path: image.path,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      setReceiving((prev) => ({
        ...prev,
        invoice_images:
          callResult.data.invoice_images ??
          (prev.invoice_images ?? []).filter(
            (existing) => existing.path !== image.path,
          ),
      }));
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message || "Invoice image deleted",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const askDeleteInvoiceImage = function (image) {
    if (!image?.path) {
      showErrorModal("Could not delete invoice image.");
      return;
    }
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteReceivingInvoiceImage(image)}
          message="Are you sure you want to delete this invoice image?"
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  const onDeleteSelectedReceivingItems = async function (reason) {
    let callResult = await makePostApiCall(`deleteReceivingItemsByMultiple`, {
      receiving_id: receiving.id,
      items: selectedItems,
      reason: reason,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving((prev) => ({ ...prev, items: newItems }));
      setSelectedItems(null);

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );

      if (selectedItems.length > 0) {
        const firstItem = selectedItems[0];
        fetchRowDetails({
          unique_id: firstItem.parent_unique_id,
          plu: firstItem.plu,
          lot_number: firstItem.lot_number,
          warehouse_location_id: firstItem.warehouse_location_id,
        });
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const askReturnSelectedItems = () => {
    if (receiving.status === 1) {
      showErrorModal("Cannot delete closed receiving.");
    } else {
      setEmptyModelOpen({
        childComponent: (
          <DeleteConfirmationForm
            buttonMessage="Delete"
            confirmAction={(reason) => onDeleteSelectedReceivingItems(reason)}
            message={`Are you sure you want to return these items?`}
            setOpen={setEmptyModelOpen}
          />
        ),
        open: true,
      });
    }
  };

  const onUpdateUnitCostForReceivingItem = async function (payload) {
    let callResult = await makePostApiCall(`receivingItems/updateUnitCost`, {
      ...payload,
      receiving_id: receiving.id,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving((prev) => ({ ...prev, items: newItems }));
      if (rowDetails[payload.unique_id]) {
        fetchRowDetails(payload);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showInlineSaveError(
          callResult.data?.message || "Could not update unit cost",
        );
      }
    }
    return callResult;
  };

  const onUpdateTotalCostForReceivingItem = async function (payload) {
    let callResult = await makePostApiCall(`receivingItems/updateTotalCost`, {
      ...payload,
      receiving_id: receiving.id,
    });
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });
      const newItems = callResult.data.receiving_items;

      setReceiving((prev) => ({ ...prev, items: newItems }));
      if (rowDetails[payload.unique_id]) {
        fetchRowDetails(payload);
      }
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      } else {
        showInlineSaveError(
          callResult.data?.message || "Could not update total cost",
        );
      }
    }
    return callResult;
  };

  const onUpdateGroupQuantities = async function (payload) {
    let callResult = await makePostApiCall(
      `receivingItems/updateGroupQuantities`,
      {
        ...payload,
        receiving_id: receiving.id,
      },
    );
    if (callResult.success) {
      setReceiving((prev) => ({
        ...prev,
        items: callResult.data.receiving_items,
      }));
    } else if (callResult.errorStatus === 401) {
      logout();
    } else {
      showInlineSaveError(
        callResult.data?.message || "Could not update receiving quantities",
      );
    }
    return callResult;
  };

  const printReceivingSummaryReport = async function () {
    let callResult = await makePostApiCall(
      `generateReceivingSummaryReport/${receivingId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const printReceivingDetailsReport = async function () {
    let callResult = await makePostApiCall(
      `generateReceivingDetailsReport/${receivingId}`,
    );

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchRowDetails = async (rowData) => {
    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `receivingItems/${receivingId}/${rowData.plu}/${rowData.lot_number}/${rowData.warehouse_location_id}/${rowData.unique_id}`,
    );
    if (callResult.success) {
      setRowDetails((prev) => ({
        ...prev,
        [rowData.unique_id]: callResult.data,
      }));
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
    setLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: false }));
  };

  const fetchSubRowDetails = async (rowData) => {
    setLastRow(rowData);
    setSubLoadingRows((prev) => ({ ...prev, [rowData.unique_id]: true }));

    let callResult = await makeGetApiCall(
      `receivingItems/pallets/${rowData.pallet_id}`,
    );
    if (callResult.success) {
      setSubRowDetails({
        ...subRowDetails,
        [rowData.unique_id]: callResult.data,
      });
      setSubLoadingRows({ ...subLoadingRows, [rowData.unique_id]: false });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const getCarriersForDropdown = async function () {
    let callResult = await makeGetApiCall(`carriersForDropdown`);
    if (callResult.success) {
      setCarrierOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const reprintReceivingItem = async function (items) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];

      try {
        const callResult = await makePostApiCallToPrinter({
          plu: item.plu,
          name_en: item.product_name,
          name_ch: item.name_ch,
          lot_number: item.lot_number,
          tare_weight: null,
          best_before_date: item.best_before_date,
          piece_count: item.total_pieces,
          weight_in_kg: item.total_weight_in_kg,
          weight_in_lbs: item.total_weight_in_lbs,
          barcode_str: item.barcode,
          label_image_zpl: item.label_image_zpl,
          is_sample: false,
        });

        if (!callResult.success) {
          if (callResult.errorStatus === 401) logout();
          showErrorModal(callResult.data?.message || "Print failed");
        }
      } catch (e) {
        showErrorModal(e?.message || "Unexpected error");
      }

      // wait 1s between jobs (skip after last if you prefer)
      if (i < items.length - 1) {
        await sleep(1000);
      }
    }
  };

  return {
    emptyModal,
    setEmptyModelOpen,
    truckOptions,
    getTruckOptionsForDropdown,
    supplierOptions,
    getSupplierOptionsForDropdown,
    getProductOptionsForDropdown,
    productOptions,
    onFetchProductDetails,
    poOptions,
    receiving,
    addItemToReceiving,
    askDeleteReceivingItemById,
    updateReceivingItem,
    openDeleteItemByBarcodePopup,
    updateReceivingInvoice,
    getReceivingInvoiceDetails,
    employeeOptions,
    getEmployeeOptionsForDropdown,
    getWarehouses,
    getWarehouseLocations,
    onDeleteReceivingInvoice,
    askDeleteInvoiceImage,
    askReturnSelectedItems,
    selectedItems,
    setSelectedItems,
    onUpdateUnitCostForReceivingItem,
    onUpdateTotalCostForReceivingItem,
    onUpdateGroupQuantities,
    printReceivingSummaryReport,
    printReceivingDetailsReport,
    showErrorModal,
    loadingRows,
    rowDetails,
    fetchRowDetails,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    getCarriersForDropdown,
    carrierOptions,
    reprintReceivingItem,
  };
}
